import { CalendarDate, Maybe } from "@sp-crm/core";
import { defaultLinkStyle } from "components/ui/link";
import { Spinner } from "components/ui/spinner";
import * as React from "react";
import { Link } from "react-router-dom";
import { useUsers } from "store/selectors/hooks";
import { useTenantSettings } from "store/selectors/settings";
import { parsePhone } from "../../util/phone";
import { NO_NAME } from "../../util/text";
import { badgeColorForNextTask, NextActionLine } from "./task-badge";
import type { ClientForDetails } from "./types";

export interface CardProps {
    client: ClientForDetails;
    highlightCard: boolean;
    pendingCard: boolean;
    showAssignedTo: boolean;
    getClientStatus: (client: ClientForDetails) => {
        descriptor: string;
        date: Maybe<CalendarDate>;
    };
}

export const EmptyCard: React.FunctionComponent = () => {
    return (
        <div className={`pipeline-card pipeline-client-card empty-card`}>
            <div className="mainline">
                <div className="title">(none)</div>
            </div>
        </div>
    );
};

const StatusDetail: React.FC<CardProps> = props => {
    const status = props.getClientStatus(props.client);
    return (
        <div className="text-xs">
            {status.descriptor}:{" "}
            {status.date.map(x => x.humanFriendly()).getOrElse("(not specified)")}
        </div>
    );
};

const SerialDetail: React.FC<CardProps> = props => {
    const { settings } = useTenantSettings();
    if (settings.showClientSerial && props.client.serial) {
        return <div className="text-md font-semibold">{props.client.serial}</div>;
    }
    return null;
};

const AssignedTo: React.FC<CardProps> = props => {
    const users = useUsers().users;

    const assignedUser = props.client.assignedUserId
        ? users[props.client.assignedUserId]
        : null;

    const text = assignedUser ? assignedUser.name : "Unassigned";

    return <p className="text-xs italic">{text}</p>;
};

const NextAction: React.FC<CardProps> = props => (
    <NextActionLine nextTask={props.client.nextTask} />
);

const Community: React.FC<CardProps> = props => {
    const chosenCommunity = props.client.chosenCommunity;
    if (!chosenCommunity) {
        return null;
    }
    return (
        <div>
            <Link
                className={defaultLinkStyle}
                to={`/communities/show/${chosenCommunity.id}`}>
                {chosenCommunity.name}
            </Link>
        </div>
    );
};

const computeClientName = (
    clientName: string | undefined | null,
    additionalClientName: string | undefined | null,
): JSX.Element => {
    if (
        (!clientName || clientName.trim().length === 0) &&
        (!additionalClientName || additionalClientName.trim().length === 0)
    ) {
        return <span>{NO_NAME}</span>;
    }
    if (!clientName || clientName.trim().length === 0) {
        return <span>{additionalClientName}</span>;
    }
    if (!additionalClientName || additionalClientName.trim().length === 0) {
        return <span>{clientName}</span>;
    }
    return (
        <span>
            {clientName}
            <br />
            {additionalClientName}
        </span>
    );
};

const ContactDisplay: React.FC<CardProps> = props => {
    const {
        settings: { showClientFirst },
    } = useTenantSettings();

    const clientName = computeClientName(
        props.client.name,
        props.client.additionalClientName,
    );
    let bestContactName = props.client.bestContactName;
    if (!bestContactName || bestContactName.trim().length === 0) {
        bestContactName = NO_NAME;
    }
    const computedBadgeColor = badgeColorForNextTask(props.client.nextTask);
    const titleName = showClientFirst ? clientName : bestContactName;
    const subtitleName = showClientFirst ? bestContactName : clientName;
    const phone = parsePhone(props.client.bestContactPhone);
    return (
        <div>
            <Link to={`/clients/show/${props.client.id}`} className={defaultLinkStyle}>
                <div className="flex gap-1 justify-between">
                    <div className="flex-1">
                        <div className="font-bold">{titleName}</div>
                        <div className="">{subtitleName}</div>
                    </div>
                    <div className="flex-none">
                        {props.pendingCard ? (
                            <Spinner />
                        ) : (
                            <div
                                className={`${computedBadgeColor} rounded-full w-3 h-3`}
                            />
                        )}
                    </div>
                </div>
            </Link>
            {phone.digits !== "" ? (
                <div>
                    <a className={defaultLinkStyle} href={"tel:" + phone.digits}>
                        {phone.display}
                    </a>
                </div>
            ) : null}
        </div>
    );
};

export const ClientCardv2: React.FC<CardProps> = props => {
    return (
        <div
            className={`space-y-2 pipeline-card pipeline-client-card ${
                props.highlightCard ? "highlight-card" : ""
            }`}>
            <ContactDisplay {...props} />
            <Community {...props} />
            <NextAction {...props} />
            <StatusDetail {...props} />
            <SerialDetail {...props} />
            {props.showAssignedTo ? <AssignedTo {...props} /> : null}
        </div>
    );
};
